/**
 * Role1Facility.js
 * PSP/CRE Tool v2.0 - Medical Facility Models
 * 
 * Role 1 Facility - Battalion Aid Station (BAS)
 * First echelon of medical care, provides point-of-injury treatment
 * and stabilization for evacuation.
 * 
 * Capabilities:
 * - Triage and initial stabilization
 * - Hemorrhage control (tourniquets)
 * - Airway management
 * - Tranexamic acid (TXA) administration
 * - IV fluid resuscitation
 * - Basic wound care
 * - NO surgical capability
 * - Hold time: Maximum 4 hours
 * 
 * Author: Dr. Tatar Radu MD
 * Institution: Romanian Military Medical Institute
 * Date: December 26, 2024
 * Version: 2.0 - Phase 2 Evacuation Asset Architecture
 * 
 * PHASE 2 CHANGES:
 * - Added organic evacuation assets (4 ambulances per Role 1)
 * - Added evacuation configuration (PUSH method typical for Role 1)
 * - Added asset management methods
 * - Integrated with evacuation chain
 */

import { BaseMedicalFacility, EVACUATION_METHODS } from './BaseMedicalFacility.js';

// ============================================================================
// ROLE 1 ORGANIC ASSET STANDARDS
// ============================================================================

/**
 * Default organic assets for Role 1 (Battalion Aid Station)
 * Based on NATO standards and Romanian Army TO&E
 * 
 * Typical Role 1 has:
 * - 2 wheeled light ambulances (road evacuation)
 * - 2 wheeled armored ambulances (forward evacuation)
 * - 8 medics (2 per ambulance + 4 at BAS)
 * - 4 drivers
 * - 8 litter bearers
 */
export const ROLE1_ORGANIC_ASSETS = {
  STANDARD: {
    ground_ambulances: {
      wheeled_light: 2,
      wheeled_armored: 2,
      tracked: 0
    },
    helicopters: {
      allocated: 0,   // Role 1 has NO organic helicopters
      on_call: 0      // May request from higher
    },
    personnel: {
      medics: 8,
      drivers: 4,
      litter_bearers: 8,
      flight_medics: 0
    }
  },
  
  LIGHT: {
    // Light infantry/airborne - fewer vehicles
    ground_ambulances: {
      wheeled_light: 2,
      wheeled_armored: 1,
      tracked: 0
    },
    helicopters: { allocated: 0, on_call: 0 },
    personnel: {
      medics: 6,
      drivers: 3,
      litter_bearers: 6,
      flight_medics: 0
    }
  },
  
  HEAVY: {
    // Armor/mechanized - tracked ambulances
    ground_ambulances: {
      wheeled_light: 1,
      wheeled_armored: 1,
      tracked: 2       // M113 ambulance or similar
    },
    helicopters: { allocated: 0, on_call: 0 },
    personnel: {
      medics: 8,
      drivers: 4,
      litter_bearers: 8,
      flight_medics: 0
    }
  },
  
  ARTILLERY: {
    // Artillery battalions - typically lighter medical support
    ground_ambulances: {
      wheeled_light: 2,
      wheeled_armored: 1,
      tracked: 0
    },
    helicopters: { allocated: 0, on_call: 0 },
    personnel: {
      medics: 6,
      drivers: 3,
      litter_bearers: 4,
      flight_medics: 0
    }
  }
};

// ============================================================================
// ROLE 1 FACILITY CLASS
// ============================================================================

export class Role1Facility extends BaseMedicalFacility {
  /**
   * Create a Role 1 facility
   * 
   * @param {Object} config - Facility configuration
   * @param {string} config.facility_id - Unique ID
   * @param {string} config.facility_name - Human-readable name
   * @param {string} config.parent_unit_id - Parent battalion ID
   * @param {string} config.parent_battalion_id - Battalion ID
   * @param {string} [config.unit_type='STANDARD'] - Parent unit type for asset allocation
   * @param {number} [config.position_feba_km=8] - Distance from FEBA
   * @param {number} [config.beds=10] - Number of beds
   */
  constructor(config) {
    // Determine organic assets based on parent unit type
    const unitType = config.unit_type || 'STANDARD';
    const organicAssets = config.organic_assets || 
                          ROLE1_ORGANIC_ASSETS[unitType] || 
                          ROLE1_ORGANIC_ASSETS.STANDARD;
    
    super({
      ...config,
      role_level: 1,
      facility_type: 'ROLE1',
      parent_unit_type: 'BATTALION',
      beds: config.beds || 10,
      surgical_teams: 0,        // Role 1 has NO surgery
      icu_beds: 0,              // Role 1 has NO ICU
      position_feba_km: config.position_feba_km !== undefined ? config.position_feba_km : 8,
      organic_assets: organicAssets,
      
      // Phase 2: Role 1 evacuation config
      evacuation_config: config.evacuation_config || {
        incoming_method: EVACUATION_METHODS.PUSH,       // POI pushes to Role 1
        outgoing_method: EVACUATION_METHODS.PUSH,       // Role 1 pushes to Role 2 (or pulled by Role 2)
        picks_up_from: [],             // Role 1 typically doesn't pull
        delivers_to: null,             // Set by force structure
        helicopter_priority: 'LOW',     // Role 1 rarely gets helicopter
        max_hold_time_hours: 4,
        triggers: {
          capacity_threshold: 80,
          time_threshold_hours: 4,
          t1_immediate_evac: true
        }
      }
    });
    
    // Role 1 specific properties
    this.parent_battalion_id = config.parent_battalion_id || config.parent_unit_id;
    this.unit_type = unitType;
    this.hold_time_hours = 4;
    this.can_perform_surgery = false;
    
    // Prehospital interventions tracking
    this.interventions_performed = {
      tourniquets_applied: 0,
      txa_administered: 0,
      airways_managed: 0,
      iv_fluids_given: 0,
      hemostatic_dressings_applied: 0
    };
  }
  
  // ============================================================================
  // OVERRIDE: GET DEFAULT ORGANIC ASSETS
  // ============================================================================
  
  _getDefaultOrganicAssets() {
    return ROLE1_ORGANIC_ASSETS[this.unit_type] || ROLE1_ORGANIC_ASSETS.STANDARD;
  }
  
  // ============================================================================
  // ROLE 1 SPECIFIC METHODS
  // ============================================================================
  
  canPerformSurgery() {
    return false;
  }
  
  /**
   * Stabilize patient for evacuation
   * Applies TCCC (Tactical Combat Casualty Care) interventions
   * 
   * @param {Object} patient - Patient object
   * @returns {Object} Result with interventions applied
   */
  stabilizePatient(patient) {
    const interventions_applied = [];
    let supply_shortages = [];
    
    // Apply tourniquet if needed
    if (patient.needs_tourniquet) {
      const result = this.consumeSupply('tourniquets', 1);
      if (result.success) {
        interventions_applied.push('TOURNIQUET');
        patient.hemorrhage_controlled = true;
        patient.survival_improvement = (patient.survival_improvement || 1.0) * 1.87;
        this.interventions_performed.tourniquets_applied++;
      } else {
        supply_shortages.push('tourniquets');
        patient.survival_improvement = (patient.survival_improvement || 1.0) * 0.08;
      }
    }
    
    // Administer TXA if needed (within 3 hours of injury)
    if (patient.needs_txa && patient.time_since_injury_hours < 3) {
      const result = this.consumeSupply('txa_doses', 1);
      if (result.success) {
        interventions_applied.push('TXA');
        patient.txa_administered = true;
        patient.survival_improvement = (patient.survival_improvement || 1.0) * 1.32;
        this.interventions_performed.txa_administered++;
      } else {
        supply_shortages.push('txa_doses');
      }
    }
    
    // Manage airway if needed
    if (patient.needs_airway) {
      const result = this.consumeSupply('airway_devices', 1);
      if (result.success) {
        interventions_applied.push('AIRWAY_MANAGEMENT');
        patient.airway_secured = true;
        patient.survival_improvement = (patient.survival_improvement || 1.0) * 1.85;
        this.interventions_performed.airways_managed++;
      } else {
        supply_shortages.push('airway_devices');
      }
    }
    
    // Apply hemostatic dressing if needed
    if (patient.needs_hemostatic_dressing) {
      const result = this.consumeSupply('hemostatic_dressings', 1);
      if (result.success) {
        interventions_applied.push('HEMOSTATIC_DRESSING');
        patient.bleeding_controlled = true;
        patient.survival_improvement = (patient.survival_improvement || 1.0) * 1.15;
        this.interventions_performed.hemostatic_dressings_applied++;
      } else {
        supply_shortages.push('hemostatic_dressings');
      }
    }
    
    // IV fluid resuscitation
    if (patient.needs_iv_fluids) {
      const result = this.consumeSupply('iv_fluids', 1);
      if (result.success) {
        interventions_applied.push('IV_FLUIDS');
        patient.iv_access = true;
        this.interventions_performed.iv_fluids_given++;
      } else {
        supply_shortages.push('iv_fluids');
      }
    }
    
    // Use trauma kit for basic stabilization
    const trauma_result = this.consumeSupply('trauma_kits', 1);
    if (!trauma_result.success) {
      supply_shortages.push('trauma_kits');
    }
    
    return {
      stabilized: interventions_applied.length > 0,
      interventions: interventions_applied,
      supply_shortages: supply_shortages,
      ready_for_evacuation: interventions_applied.length > 0,
      survival_modifier: patient.survival_improvement || 1.0,
      critical_shortages: supply_shortages.length > 0
    };
  }
  
  /**
   * Process T3 (minimal) casualties
   * Many can return to duty (RTD) after basic treatment
   */
  processMinimalCasualties(t3_count) {
    const rtd_count = Math.floor(t3_count * 0.70);
    const evacuate_count = t3_count - rtd_count;
    
    this.consumeSupply('bandages', t3_count * 2);
    this.consumeSupply('antibiotics', Math.floor(t3_count * 0.5));
    
    this.stats.total_returned_to_duty += rtd_count;
    
    return {
      total_t3: t3_count,
      return_to_duty: rtd_count,
      evacuate: evacuate_count,
      rtd_percentage: (rtd_count / t3_count) * 100
    };
  }
  
  /**
   * Get intervention statistics
   */
  getInterventionStatistics() {
    const total_interventions = Object.values(this.interventions_performed)
      .reduce((sum, count) => sum + count, 0);
    
    return {
      ...this.interventions_performed,
      total_interventions: total_interventions,
      interventions_per_patient: this.stats.total_patients_treated > 0 
        ? total_interventions / this.stats.total_patients_treated 
        : 0
    };
  }
  
  isWithinHoldTime(hours_since_admission) {
    return hours_since_admission <= this.hold_time_hours;
  }
  
  getCriticalSupplies() {
    return [
      'tourniquets',
      'txa_doses',
      'trauma_kits',
      'airway_devices',
      'hemostatic_dressings',
      'iv_fluids'
    ];
  }
  
  // ============================================================================
  // PHASE 2: EVACUATION ASSET MANAGEMENT
  // ============================================================================
  
  /**
   * Request evacuation asset from organic pool
   * Tracks mission and reduces available assets temporarily
   * 
   * @param {string} assetType - 'wheeled_light', 'wheeled_armored', or 'tracked'
   * @returns {Object} Result with asset assignment
   */
  dispatchAmbulance(assetType = 'wheeled_armored') {
    const effective = this.getEffectiveAssets();
    
    if (effective.ground_ambulances[assetType] <= 0) {
      // Try other available types
      const alternatives = ['wheeled_armored', 'wheeled_light', 'tracked'];
      for (const alt of alternatives) {
        if (alt !== assetType && effective.ground_ambulances[alt] > 0) {
          assetType = alt;
          break;
        }
      }
      
      if (effective.ground_ambulances[assetType] <= 0) {
        return {
          success: false,
          error: 'NO_AMBULANCES_AVAILABLE',
          effective_assets: effective
        };
      }
    }
    
    // Track mission (in real implementation, this would update a missions array)
    this.stats.ground_evac_missions++;
    
    return {
      success: true,
      asset_type: assetType,
      facility_id: this.facility_id,
      mission_id: `evac_${this.facility_id}_${this.stats.ground_evac_missions}`,
      timestamp: new Date().toISOString()
    };
  }
  
  /**
   * Get asset utilization status for this Role 1
   */
  getAssetUtilization() {
    const effective = this.getEffectiveAssets();
    const organic = this._getDefaultOrganicAssets();
    
    // Calculate how many assets are currently on mission
    // (simplified - in real implementation would track active missions)
    const utilizationPercent = this.stats.ground_evac_missions > 0 ? 
      Math.min(100, (this.stats.ground_evac_missions / 10) * 100) : 0;
    
    return {
      facility_id: this.facility_id,
      role_level: 1,
      organic: organic,
      effective: effective,
      missions_completed: this.stats.ground_evac_missions,
      current_utilization_percent: utilizationPercent,
      status: utilizationPercent > 80 ? 'STRAINED' : 
              utilizationPercent > 50 ? 'MODERATE' : 'NORMAL'
    };
  }
  
  // ============================================================================
  // OVERRIDE METHODS
  // ============================================================================
  
  getOperationalIssues() {
    const issues = super.getOperationalIssues();
    
    // Check critical Role 1 supplies
    const critical = this.getCriticalSupplies();
    critical.forEach(supply => {
      if (this.supplies[supply] === 0) {
        issues.push(`No ${supply.replace(/_/g, ' ')}`);
      } else if (this.supplies[supply] < 10) {
        issues.push(`Low ${supply.replace(/_/g, ' ')} (< 10)`);
      }
    });
    
    // Check evacuation assets
    const effective = this.getEffectiveAssets();
    if (effective.ground_ambulances.total < 2) {
      issues.push('Critically low ambulances (< 2)');
    }
    
    return issues;
  }
  
  toJSON() {
    return {
      ...super.toJSON(),
      parent_battalion_id: this.parent_battalion_id,
      unit_type: this.unit_type,
      hold_time_hours: this.hold_time_hours,
      can_perform_surgery: this.can_perform_surgery,
      interventions_performed: this.interventions_performed,
      intervention_statistics: this.getInterventionStatistics(),
      asset_utilization: this.getAssetUtilization()
    };
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

// Already exported at definition

export default Role1Facility;
